import axios from 'axios';
import type { CacheRequestConfig } from 'axios-cache-interceptor';
import type {
  CapabilityLevel,
  CapabilityPair,
  LevelWorking
} from '../pages/new-designs/tabs/Program/components/learning-lifecycle/teamCapability';
import type { LevelCriteria } from '../pages/new-designs/tabs/capability-setup/capabilitySetup';
import type { CapabilitySource } from '../pages/new-designs/tabs/capability-setup/capabilitySource';

// The Capability backend (BE shared/services/capability, src/service/capability):
// the learner's own levels, Roleplays and Assessments, and the admin's
// dashboard, Members, Insights and Operations data. Authentication and the
// region go on every request through the app's axios interceptor (App.tsx).
// `orgId` names the org for platform admins only; everyone else's token
// carries it. Assessment ids are "assessment:<id>" on the page and the bare id
// in a path.

const base = () => `${process.env.REACT_APP_API}`;
const query = (params: Record<string, string | undefined>) => {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`)
    .join('&');
  return qs ? `?${qs}` : '';
};
const teamsQuery = (teamIds?: string[] | null, orgId?: string) =>
  query({ teamIds: teamIds?.length ? teamIds.join(',') : undefined, orgId });
const bareId = (assessmentId: string) => assessmentId.replace(/^assessment:/, '');
const bareDraftId = (draftId: string) => draftId.replace(/^assessment-draft:/, '');
const data = <T>(p: Promise<{ data: T }>) => p.then((r) => r.data);
// React Query is these reads' cache (src/queries/useCapability.ts). The shared axios is also set up
// with axios-cache-interceptor (api/index.ts), which answers a GET from memory for 5 minutes, so a
// refetch after a save, a run or a discard would get the answer from before it.
const fresh: CacheRequestConfig = { cache: false };

// ─── Shapes ─────────────────────────────────────────────────────────────────

/** One capability of the person's role: their level, the working behind it, where they stood before. */
export interface CapabilityMeasureView {
  id: string;
  capabilityName: string;
  /** Where the capability comes from: a role's own, or a shared Development or Standards one. Absent: a role's own. */
  source?: CapabilitySource;
  criteria: LevelCriteria;
  working: LevelWorking;
  previousLevel: CapabilityLevel | null;
  /** The learner's own view only. */
  gradedLeftThisWeek?: number;
}

export interface MyCapabilityView {
  roleId: string;
  roleName: string;
  teamId: string;
  teamName: string;
  gradedPerWeek: number;
  measures: CapabilityMeasureView[];
}

export interface TrendView {
  weeks: Array<{ weekStart: string; count: number; total: number; percent: number | null }>;
  events: Array<{
    weekStart: string;
    kind: 'assessment' | 'training';
    title: string;
    launchedOn: string;
    completionRate: number;
    capabilities: string[];
  }>;
}

/** One week of the Activity trend: its Monday and the Activity % at its end. */
export interface ActivityHistoryPoint {
  weekStart: string;
  score: number;
}

export interface ActivitySectionView {
  key: 'roleplays' | 'practiceRounds';
  name: 'Roleplays' | 'Practice Rounds';
  value: string;
  pct: number;
  count: number;
  medal: 'wood' | 'bronze' | 'silver' | 'gold' | 'platinum';
}

export type LeadersView = Record<
  string,
  { thisWeek: Array<{ name: string; value: string }>; allTime: Array<{ name: string; value: string }> }
>;

export interface SessionStartView {
  sessionId: string;
  kind: 'graded' | 'practice';
  situation: string;
  attemptsAllowed: number;
  gradedLeftThisWeek: number;
}

export interface RecordedRoleplayView {
  id: string;
  kind: 'graded' | 'practice';
  capabilityId: string;
  capabilityName: string;
  level: CapabilityLevel;
  analysis: string;
  criteria: LevelCriteria;
  completedOn: string;
  source: string;
}

export interface AttemptView {
  attemptNumber: number;
  attemptsAllowed: number;
  level: CapabilityLevel;
  feedback: string;
  finished: boolean;
  recorded: RecordedRoleplayView | null;
  gradedLeftThisWeek: number;
}

export interface AssessmentCardView {
  id: string;
  name: string;
  ownerName: string | null;
  stageId: string | null;
  category: string | null;
  message: string;
  createdAt: string;
  items: Array<{ capabilityId: string; capabilityName: string }>;
  progress: {
    items: Record<string, { roleplayDone: boolean; quizDone: boolean; level: CapabilityLevel | null }>;
    completedAt: string | null;
  };
  status: 'not-started' | 'in-progress' | 'completed';
}

export interface AssessmentPlayerView {
  id: string;
  name: string;
  ownerName: string | null;
  message: string;
  createdAt: string;
  completedAt: string | null;
  items: Array<{
    capabilityId: string;
    capabilityName: string;
    roleplay: string;
    quiz: { question: string; options: string[] };
    progress: { roleplay: { id: string; response: string } | null; quizAnswer: number | null };
  }>;
}

export interface AssessmentResultsView {
  completedAt: string;
  items: Array<{
    capabilityId: string;
    capabilityName: string;
    roleplay: {
      id: string;
      level: CapabilityLevel;
      completedOn: string;
      source: string;
      analysis: string;
      criteria: LevelCriteria;
    };
    quiz: { answerIndex: number; passed: boolean; correctIndex: number; correctOption: string };
    levelNow: CapabilityLevel | null;
    previousLevel: CapabilityLevel | null;
    recentRoleplays: Array<{ id: string; level: CapabilityLevel; completedOn: string; source: string }>;
  }>;
}

export interface CapabilityPersonView {
  id: string;
  name: string;
  teamId: string;
  teamName: string;
  roleId: string | null;
  roleName: string | null;
}

export interface PairsView {
  teams: Array<{ id: string; name: string }>;
  people: CapabilityPersonView[];
  pairs: CapabilityPair[];
}

export interface LevelRowView {
  id: string;
  teamId: string;
  teamName: string;
  roleId: string;
  roleName: string;
  capabilityId: string;
  capabilityName: string;
  /** Where the capability comes from: a role's own, or a shared Development or Standards one. Absent: a role's own. */
  source?: CapabilitySource;
  criteria: LevelCriteria;
  ratio: { count: number; total: number; percent: number | null };
  employees: Array<{ id: string; name: string; working: LevelWorking }>;
}

export interface MemberSummaryView {
  id: string;
  capability: {
    id: string;
    roleName: string | null;
    teamName: string | null;
    percent: number | null;
    strongest: { name: string; level: CapabilityLevel } | null;
    weakest: { name: string; level: CapabilityLevel } | null;
  } | null;
  activitySections: ActivitySectionView[];
  elliottQuestions: number;
  roleplays: number;
}

export interface MemberCapabilityView {
  member: { id: string; name: string };
  roleName: string;
  teamName: string;
  measures: CapabilityMeasureView[];
  trend: TrendView;
}

export interface MemberConversationView {
  id: string;
  title: string;
  updatedAt: number;
  messages: Array<{ role: 'member' | 'elliott'; text: string }>;
}

export interface InsightsView extends PairsView {
  observed: CapabilityPair[];
  activity: Array<{ id: string; measures: Record<string, number> }>;
}

export interface OperationsRoleView {
  id: string;
  teamId: string;
  name: string;
  ready: boolean;
  capabilities: Array<{ id: string; name: string; criteria: LevelCriteria; source?: CapabilitySource }>;
}

export interface OperationsPersonView extends CapabilityPersonView {
  email: string;
  invitePending: boolean;
}

export interface OperationsView {
  teams: Array<{ id: string; name: string; confirmedAt: string | null }>;
  roles: OperationsRoleView[];
  people: OperationsPersonView[];
  /** Members of the org outside the teams in view, to add to one. */
  others: Array<{ id: string; name: string; email: string }>;
  observed: Record<string, Record<string, { level: CapabilityLevel; observedAt: string; observedByName: string }>>;
}

export interface TeamChangeInput {
  personId: string;
  teamId: string;
  status: 'same' | 'changed' | 'left';
  roleId?: string;
}

export type AddMemberInput =
  | { userId: string; roleId: string; access: 'learner' | 'admin' | 'manager' }
  | { firstName: string; lastName?: string; email: string; roleId: string; access: 'learner' | 'admin' | 'manager' };

export interface DraftItemView {
  capabilityId: string;
  capabilityName: string;
  /** Where the capability comes from: a role's own, or a shared Development or Standards one. Absent: a role's own. */
  source?: CapabilitySource;
  roleId: string;
  roleName: string;
  teamName: string;
  levels: LevelCriteria;
  roleplay: string;
  quiz: { question: string; options: string[]; correct: number };
}

export interface RunAssessmentInput {
  name: string;
  stageId: string | null;
  category: string | null;
  ownerId: string | null;
  publishBy: string | null;
  docIds: string[];
  audienceLabel: string;
  audience: {
    allLearners: boolean;
    roleIds: string[];
    roleMatch: 'any' | 'all';
    rules: Array<{ listId: string; listName?: string; matchType: 'any' | 'all'; valueIds: string[] }>;
  };
  schedule: 'none' | 'monthly' | 'quarterly' | 'halfYearly' | 'yearly';
  firstRun: string | null;
  message: string;
  items: Array<{
    capabilityId: string;
    roleplay: string;
    quiz: { question: string; options: string[]; correct: number };
  }>;
}

export interface AdminAssessmentView {
  id: string;
  name: string;
  ownerName: string | null;
  stageId: string | null;
  category: string | null;
  audienceLabel: string;
  roleIds: string[];
  schedule: RunAssessmentInput['schedule'];
  firstRun: string;
  message: string;
  items: DraftItemView[];
  createdAt: string;
  status: 'running' | 'closed';
  run: { index: number; startsAt: string; started: number; completed: number } | null;
  /** The teams of the roles it measures. Not sent by a backend older than the dashboard's Assessment cards. */
  teamIds?: string[];
  /** How many people hold a role it measures today: what its completion is counted over. */
  audience?: number;
}

/** An Assessment that is still being built (not yet run), as the backend keeps it. */
export interface AssessmentDraftView {
  /** `assessment-draft:<id>`. */
  id: string;
  name: string;
  stageId: string | null;
  roleIds: string[];
  /** The teams of the roles it measures. */
  teamIds: string[];
  ownerName: string | null;
  /** Run Assessment's own state, as the flow sent it. */
  state: unknown;
  /** The viewer may carry on building it, discard it or run it. */
  canEdit: boolean;
  updatedAt: string;
  updatedBy: string;
}

export interface SaveAssessmentDraftInput {
  name: string;
  stageId: string | null;
  roleIds: string[];
  ownerName: string | null;
  state: object;
}

/** A Training Elliott recommends for a role on one of its capabilities, with the figures and his reason. */
export interface CapabilityRecommendationView {
  /** `${roleId}|${capabilityId}`. */
  id: string;
  teamId: string;
  teamName: string;
  roleId: string;
  roleName: string;
  capabilityId: string;
  capabilityName: string;
  /** Where the capability comes from: a role's own, or a shared Development or Standards one. Absent: a role's own. */
  source?: CapabilitySource;
  /** People who hold the role, of them those with a level, and of those the ones at Capable or better. */
  employees: number;
  assessed: number;
  capable: number;
  percent: number;
  course: { name: string; purpose: string };
  /** What Elliott has found, what he recommends, and why it helps. */
  reason: { identified: string; recommendation: string; benefit: string };
}

export interface TrainingInput {
  name: string;
  stage?: string;
  category: string | null;
  purpose: string;
  audience: string;
  capabilities: Array<{ name: string; levels?: LevelCriteria }>;
  learningOutcomes: string[];
  knowledgeSources?: Array<{ title: string; type: string }>;
  format?: 'course' | 'shortCourse' | 'brief';
}

// ─── Calls ──────────────────────────────────────────────────────────────────

export const capabilityApi = {
  // The learner's own.
  myCapability: () => data<{ capability: MyCapabilityView | null }>(axios.get(`${base()}/v3/member/capability`, fresh)),
  myTrend: () => data<TrendView>(axios.get(`${base()}/v3/member/capability/trend`, fresh)),
  myActivity: () =>
    data<{ sections: ActivitySectionView[]; leaders: LeadersView }>(
      axios.get(`${base()}/v3/member/capability/activity`, fresh)
    ),
  myActivityHistory: () =>
    data<{ history: ActivityHistoryPoint[] }>(axios.get(`${base()}/v3/member/capability/activity/history`, fresh)),
  startRoleplay: (body: { capabilityId: string; kind: 'graded' | 'practice' }) =>
    data<SessionStartView>(axios.post(`${base()}/v3/member/capability/roleplays`, body)),
  answerRoleplay: (sessionId: string, response: string) =>
    data<AttemptView>(axios.post(`${base()}/v3/member/capability/roleplays/${sessionId}/attempts`, { response })),
  myAssessments: () => data<{ assessments: AssessmentCardView[] }>(axios.get(`${base()}/v3/member/assessments`, fresh)),
  myAssessment: (assessmentId: string) =>
    data<AssessmentPlayerView>(axios.get(`${base()}/v3/member/assessments/${bareId(assessmentId)}`, fresh)),
  answerAssessmentRoleplay: (
    assessmentId: string,
    capabilityId: string,
    body: { response: string; inputMode: 'text' | 'voice' }
  ) =>
    data<{ roleplayId: string; completedOn: string }>(
      axios.post(
        `${base()}/v3/member/assessments/${bareId(assessmentId)}/items/${encodeURIComponent(capabilityId)}/roleplay`,
        body
      )
    ),
  answerAssessmentQuiz: (assessmentId: string, capabilityId: string, answerIndex: number) =>
    data<{ quizAnswer: number; assessmentCompleted: boolean; completedAt: string | null }>(
      axios.post(
        `${base()}/v3/member/assessments/${bareId(assessmentId)}/items/${encodeURIComponent(capabilityId)}/quiz`,
        { answerIndex }
      )
    ),
  myAssessmentResults: (assessmentId: string) =>
    data<AssessmentResultsView>(axios.get(`${base()}/v3/member/assessments/${bareId(assessmentId)}/results`, fresh)),

  // The admin's.
  pairs: (teamIds?: string[] | null, orgId?: string) =>
    data<PairsView>(axios.get(`${base()}/v3/admin/capability/pairs${teamsQuery(teamIds, orgId)}`, fresh)),
  levels: (teamIds?: string[] | null, orgId?: string) =>
    data<{ rows: LevelRowView[] }>(
      axios.get(`${base()}/v3/admin/capability/levels${teamsQuery(teamIds, orgId)}`, fresh)
    ),
  trend: (teamIds?: string[] | null, orgId?: string) =>
    data<TrendView>(axios.get(`${base()}/v3/admin/capability/trend${teamsQuery(teamIds, orgId)}`, fresh)),
  activity: (teamIds?: string[] | null, orgId?: string) =>
    data<{ sections: ActivitySectionView[]; leaders: LeadersView; learnerCount: number }>(
      axios.get(`${base()}/v3/admin/capability/activity${teamsQuery(teamIds, orgId)}`, fresh)
    ),
  activityHistory: (teamIds?: string[] | null, orgId?: string) =>
    data<{ history: ActivityHistoryPoint[] }>(
      axios.get(`${base()}/v3/admin/capability/activity/history${teamsQuery(teamIds, orgId)}`, fresh)
    ),
  memberActivityHistory: (userId: string, orgId?: string) =>
    data<{ history: ActivityHistoryPoint[] }>(
      axios.get(`${base()}/v3/admin/capability/members/${userId}/activity/history${query({ orgId })}`, fresh)
    ),
  members: (orgId?: string) =>
    data<{ members: MemberSummaryView[] }>(
      axios.get(`${base()}/v3/admin/capability/members${query({ orgId })}`, fresh)
    ),
  member: (userId: string, orgId?: string) =>
    data<MemberCapabilityView>(axios.get(`${base()}/v3/admin/capability/members/${userId}${query({ orgId })}`, fresh)),
  memberConversations: (userId: string, orgId?: string) =>
    data<{ member: { id: string; name: string }; conversations: MemberConversationView[]; total: number }>(
      axios.get(`${base()}/v3/admin/capability/members/${userId}/conversations${query({ orgId })}`, fresh)
    ),
  insights: (teamIds?: string[] | null, orgId?: string) =>
    data<InsightsView>(axios.get(`${base()}/v3/admin/capability/insights${teamsQuery(teamIds, orgId)}`, fresh)),
  operations: (teamIds?: string[] | null, orgId?: string) =>
    data<OperationsView>(axios.get(`${base()}/v3/admin/capability/operations${teamsQuery(teamIds, orgId)}`, fresh)),
  observe: (userId: string, capabilityId: string, level: CapabilityLevel | null, orgId?: string) =>
    data<{ level: CapabilityLevel | null; observedAt: string }>(
      axios.put(
        `${base()}/v3/admin/capability/observations/${userId}/${encodeURIComponent(capabilityId)}${query({ orgId })}`,
        { level }
      )
    ),
  confirmTeams: (teamIds: string[], changes: TeamChangeInput[], orgId?: string) =>
    data<OperationsView>(
      axios.post(`${base()}/v3/admin/capability/teams/confirm${query({ orgId })}`, { teamIds, changes })
    ),
  addMember: (teamId: string, body: AddMemberInput, orgId?: string) =>
    data<OperationsView>(axios.post(`${base()}/v3/admin/capability/teams/${teamId}/members${query({ orgId })}`, body)),
  teamMembers: (teamId: string, orgId?: string) =>
    data<{
      people: Array<{ id: string; name: string; email: string; roleId: string | null; roleName: string | null }>;
    }>(axios.get(`${base()}/v3/admin/capability/teams/${teamId}/members${query({ orgId })}`, fresh)),
  draftAssessment: (
    // roleIds: the audience's roles, so a shared capability is drafted for those roles only.
    body: {
      name: string;
      category: string | null;
      audience: string;
      docIds: string[];
      capabilityIds: string[];
      roleIds?: string[];
    },
    orgId?: string
  ) =>
    data<{ items: DraftItemView[] }>(
      axios.post(`${base()}/v3/admin/capability/assessments/draft${query({ orgId })}`, body)
    ),
  /** `draftId`: the draft it was built from, which the backend forgets once the Assessment is run. */
  runAssessment: (body: RunAssessmentInput, orgId?: string, draftId?: string) =>
    data<AdminAssessmentView>(
      axios.post(
        `${base()}/v3/admin/capability/assessments${query({
          orgId,
          draftId: draftId ? bareDraftId(draftId) : undefined
        })}`,
        body
      )
    ),
  assessments: (orgId?: string) =>
    data<{ assessments: AdminAssessmentView[] }>(
      axios.get(`${base()}/v3/admin/capability/assessments${query({ orgId })}`, fresh)
    ),
  closeAssessment: (assessmentId: string, orgId?: string) =>
    axios.delete(`${base()}/v3/admin/capability/assessments/${bareId(assessmentId)}${query({ orgId })}`),
  /** The Assessments still being built that the viewer may see. */
  assessmentDrafts: (orgId?: string) =>
    data<{ drafts: AssessmentDraftView[] }>(
      axios.get(`${base()}/v3/admin/capability/assessment-drafts${query({ orgId })}`, fresh)
    ),
  /** Keeps an Assessment that is being built, creating it the first time. */
  saveAssessmentDraft: (draftId: string, body: SaveAssessmentDraftInput, orgId?: string) =>
    data<AssessmentDraftView>(
      axios.put(`${base()}/v3/admin/capability/assessment-drafts/${bareDraftId(draftId)}${query({ orgId })}`, body)
    ),
  discardAssessmentDraft: (draftId: string, orgId?: string) =>
    axios.delete(`${base()}/v3/admin/capability/assessment-drafts/${bareDraftId(draftId)}${query({ orgId })}`),
  draftLevels: (body: { roleName: string; teamName?: string; capabilities: string[] }, orgId?: string) =>
    data<{ capabilities: Array<{ name: string; criteria: LevelCriteria }> }>(
      axios.post(`${base()}/v3/admin/capability-setup/draft-levels${query({ orgId })}`, body)
    ),
  suggestCapabilities: (body: { roleName: string; teamName?: string; existing: string[] }, orgId?: string) =>
    data<{ capabilities: string[] }>(
      axios.post(`${base()}/v3/admin/capability-setup/suggest-capabilities${query({ orgId })}`, body)
    ),
  /** Elliott's four levels for capabilities of a shared set (Development or Standards), for everyone it applies to. */
  draftSharedLevels: (kind: 'development' | 'standards', body: { capabilities: string[] }, orgId?: string) =>
    data<{ capabilities: Array<{ name: string; criteria: LevelCriteria }> }>(
      axios.post(`${base()}/v3/admin/capability-setup/sets/${kind}/draft-levels${query({ orgId })}`, body)
    ),
  /** Elliott's suggestions for a shared set, none of them a name already in use in the organization (`existing`). */
  suggestShared: (kind: 'development' | 'standards', body: { existing: string[] }, orgId?: string) =>
    data<{ capabilities: string[] }>(
      axios.post(`${base()}/v3/admin/capability-setup/sets/${kind}/suggest-capabilities${query({ orgId })}`, body)
    ),
  recommendations: (teamIds?: string[] | null, orgId?: string) =>
    data<{ recommendations: CapabilityRecommendationView[] }>(
      axios.get(`${base()}/v3/admin/capability/recommendations${teamsQuery(teamIds, orgId)}`, fresh)
    ),
  /** Dismisses a recommended Training (ignored, or published once it is being built); null brings it back. */
  setRecommendationStatus: (
    roleId: string,
    capabilityId: string,
    status: 'ignored' | 'published' | null,
    orgId?: string
  ) =>
    data<{ status: 'ignored' | 'published' | null }>(
      axios.put(
        `${base()}/v3/admin/capability/recommendations/${roleId}/${encodeURIComponent(capabilityId)}${query({
          orgId
        })}`,
        { status }
      )
    ),
  /** Encourage Practice: nudges one person (in the bell, and in Microsoft Teams when connected) to practise a capability. */
  encouragePractice: (userId: string, capabilityId: string, orgId?: string) =>
    data<{ sentAt: string; inApp: boolean; teams: boolean }>(
      axios.post(`${base()}/v3/admin/capability/members/${userId}/practice-nudge${query({ orgId })}`, { capabilityId })
    ),
  /** goalLessons: pick under the shared capabilities' build rules (every goal a lesson of its own). */
  trainingFormat: (body: TrainingInput & { goalLessons?: boolean }, orgId?: string) =>
    data<{ format: 'course' | 'shortCourse' | 'brief'; reason: string }>(
      axios.post(`${base()}/v3/admin/capability/trainings/format${query({ orgId })}`, body)
    ),
  trainingPreview: (body: TrainingInput, orgId?: string) =>
    data<{ text: string }>(axios.post(`${base()}/v3/admin/capability/trainings/preview${query({ orgId })}`, body))
};

/**
 * The backend's message on a failed call, for the page to show; the page's own
 * when the answer has none. Never axios's "Request failed with status code …".
 */
export const apiMessage = (err: any, fallback: string): string => {
  const message = err?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};
